import { maybe, Maybe, rd } from '@passionware/monads';
import { useQueries } from '@tanstack/react-query';
import { AppScopeIdentity } from 'v5/core/app-scope/appScope.types';
import { SHELL_FEATURE_FLAGS } from 'v5/core/app-scope/shellFeatureFlags';
import { AtellioFeatureGroupDefinition } from 'v5/features';
import {
  FeatureFlagFinalSource,
  FeatureName,
  FeatureValueById
} from 'v5/platform/feature-management/feature-management';
import { FeatureService } from './FeatureService';

/**
 * Shell chrome flags always read the organization (legacy) set, even when the
 * caller is browsing a workspace/team. Absent scope stays absent.
 */
export function resolveFeatureFlagScope(
  scope: Maybe<AppScopeIdentity>,
  featureId: FeatureName<AtellioFeatureGroupDefinition>
): Maybe<AppScopeIdentity> {
  if (!SHELL_FEATURE_FLAGS.has(featureId)) {
    return scope;
  }
  return maybe.map(scope, item => ({
    level: 'organization' as const,
    organizationId: item.organizationId
  }));
}

export function createFeatureService(
  flagSource: FeatureFlagFinalSource<
    AtellioFeatureGroupDefinition,
    Maybe<AppScopeIdentity>
  >
): FeatureService {
  return {
    useFeatureFlags: scope => flagSource.useFeatureFlags(scope),
    useFeatureFlagsQueries: scopes =>
      useQueries({
        queries: scopes.map(scope => ({
          queryKey: ['featureService', 'featureFlags', scope] as const,
          queryFn: () => flagSource.ensureFeatureFlags(scope)
        }))
      }),
    ensureFeatureFlags: scope => flagSource.ensureFeatureFlags(scope),
    useFeature: (scope, featureId) => {
      const queryScope = resolveFeatureFlagScope(scope, featureId);
      return rd.useMemoMap(
        flagSource.useFeatureFlags(queryScope),
        (flags, id) => flags[id],
        featureId
      );
    },
    ensureFeature: async (scope, featureId) => {
      const flags = await flagSource.ensureFeatureFlags(
        maybe.getOrThrow(resolveFeatureFlagScope(scope, featureId))
      );
      return flags[featureId];
    }
  };
}
